/**
 * Vehicles API — Sprint I.
 *
 * Two surfaces share the same client:
 *   - JWT-authenticated /v1/vehicles* (list / get / status)
 *   - The telematics webhook is provider-side (no UI client needed)
 *
 * Pagination is intentionally simple (cap at 500) — at fleet scale we'll
 * switch to cursor pagination, but most operators have <100 vehicles.
 */

import { api, type RequestOptions } from "./client";


export type VehicleStatus = "idle" | "busy" | "offline" | "maintenance";


export interface VehicleDto {
  id:                   string;
  supplier_id:          string;
  driver_id:            string | null;
  display_name:         string;
  license_plate:        string | null;
  vehicle_type:         string;
  specs:                Record<string, unknown>;
  status:               VehicleStatus;
  current_lat:          number | null;
  current_lng:          number | null;
  current_speed_kmh:    number | null;
  current_heading_deg:  number | null;
  last_seen_at:         string | null;
  assigned_listing_id:  string | null;
  created_at:           string;
  updated_at:           string;
}


export interface TelematicsEventDto {
  id:           string;
  recorded_at:  string;
  received_at:  string;
  lat:          number;
  lng:          number;
  speed_kmh:    number | null;
  heading_deg:  number | null;
}


export interface VehicleDetailDto extends VehicleDto {
  recent_pings: TelematicsEventDto[];
}


export interface VehicleCreateRequest {
  supplier_id:    string;
  driver_id?:     string;
  display_name:   string;
  license_plate?: string;
  vehicle_type:   string;
  specs?:         Record<string, unknown>;
  status?:        VehicleStatus;
}


export interface StatusUpdateRequest {
  status: VehicleStatus;
  reason?: string;
}


export const vehiclesApi = {
  list(params: {
    status?: VehicleStatus;
    limit?: number;
  } = {}, opts?: RequestOptions): Promise<VehicleDto[]> {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    q.set("limit", String(params.limit ?? 200));
    return api.get<VehicleDto[]>(`/vehicles?${q.toString()}`, opts);
  },

  get(
    vehicleId: string, pingLimit = 50, opts?: RequestOptions,
  ): Promise<VehicleDetailDto> {
    return api.get<VehicleDetailDto>(
      `/vehicles/${vehicleId}?ping_limit=${pingLimit}`, opts,
    );
  },

  create(body: VehicleCreateRequest, opts?: RequestOptions): Promise<VehicleDto> {
    return api.post<VehicleDto>("/vehicles", body, opts);
  },

  updateStatus(
    vehicleId: string, body: StatusUpdateRequest, opts?: RequestOptions,
  ): Promise<VehicleDto> {
    return api.post<VehicleDto>(`/vehicles/${vehicleId}/status`, body, opts);
  },
};
